import { ReactNode, UIEvent, useState } from 'react';

import { MplTopSectionHeader } from '../../organisms/MplTopSectionHeader';

import {
	StyledDTicketPageLayoutContainer,
	StyledDTicketPageLayoutContent,
	StyledSafeAreaHeader,
} from './DTicketPageLayout.styled';

export interface IDTicketPageLayoutProps {
	/**
	 * Page title displayed in the header
	 */
	title: string;
	/**
	 * Callback when back button is clicked
	 */
	onBack: () => void;
	/**
	 * Optional URL for info icon in header
	 */
	infoUrl?: string;
	/**
	 * Main content of the page
	 */
	children: ReactNode;
	/**
	 * Whether the main content area should be scrollable
	 * - true: Content scrolls within the container (default)
	 * - false: Content is fixed, internal elements handle their own scroll
	 */
	scrollable?: boolean;
	/**
	 * Optional data-testid for the container
	 */
	dataTestId?: string;
	/**
	 * When true, renders a skeleton placeholder at the title slot to keep
	 * header height stable while the title resolves.
	 */
	isTitleLoading?: boolean;
	/**
	 * When true (the default), the header is pinned (sticky) to the top while
	 * the content scrolls beneath it, so the title/back bar — and the top safe
	 * area it carries — always covers the status bar and never overlaps the
	 * scrolling content. Pass false only for layouts that intentionally let the
	 * header scroll away.
	 */
	stickyHeader?: boolean;
}

/**
 * Reusable page layout for DTicket-related pages.
 *
 * Handles:
 * - Top safe area (notch, status bar) on iOS/Android
 * - Bottom safe area (home indicator, navigation bar) on iOS/Android
 * - Consistent padding across all platforms
 * - Mobile browser viewport handling (Safari, Chrome)
 *
 * Usage patterns:
 * 1. Scrollable content (UserDTicket): scrollable=true (default)
 * 2. Fixed content with internal scroll (DownloadInvoices): scrollable=false
 * 3. Fixed content without scroll (SubscriptionPause): scrollable=false
 */
export const DTicketPageLayout = ({
	title,
	onBack,
	infoUrl,
	children,
	scrollable = true,
	dataTestId,
	isTitleLoading,
	stickyHeader = true,
}: IDTicketPageLayoutProps) => {
	// Only elevate the sticky header once the content is actually scrolled.
	const [isScrolled, setIsScrolled] = useState(false);
	const handleScroll = (e: UIEvent<HTMLDivElement>) => {
		setIsScrolled(e.currentTarget.scrollTop > 0);
	};

	return (
		<StyledDTicketPageLayoutContainer
			data-testid={dataTestId}
			onScroll={stickyHeader ? handleScroll : undefined}
		>
			<StyledSafeAreaHeader
				sticky={stickyHeader}
				elevated={stickyHeader && isScrolled}
			>
				<MplTopSectionHeader
					title={title}
					onBack={onBack}
					infoUrl={infoUrl}
					dataTestId={dataTestId}
					isTitleLoading={isTitleLoading}
				/>
			</StyledSafeAreaHeader>

			<StyledDTicketPageLayoutContent scrollable={scrollable}>
				{children}
			</StyledDTicketPageLayoutContent>
		</StyledDTicketPageLayoutContainer>
	);
};
